import React from 'react';
import {
  UserCog,
  Lock,
  FileCheck,
  Map,
  Repeat,
  LineChart,
} from 'lucide-react';
import { Reveal, useSheen } from './Reveal';

const PILLARS = [
  {
    icon: UserCog,
    title: 'Dedicated Account Management',
    desc: 'A single, Director-level point of contact owns your engagement end-to-end — strategy, submission, reporting. No handoffs, no juniors on your bids.',
    accent: 'blue',
  },
  {
    icon: Lock,
    title: 'NDA-Protected Engagements',
    desc: 'Every bid is executed under enterprise-grade confidentiality. Pricing, strategy, and win data stay inside your organization — protected across our team and systems.',
    accent: 'violet',
  },
  {
    icon: FileCheck,
    title: 'ISO-Grade Documentation',
    desc: 'Audit-ready submission trails, compliance checklists, and version-controlled proposal libraries — the kind of paper trail procurement committees and internal audit teams expect.',
    accent: 'emerald',
  },
  {
    icon: Map,
    title: 'Multi-State Coverage',
    desc: 'An active bid desk operating across Gujarat, Maharashtra, Delhi NCR, and Karnataka — with on-ground documentation support when physical submission is required.',
    accent: 'amber',
  },
  {
    icon: Repeat,
    title: 'End-to-End Bid Lifecycle',
    desc: 'From opportunity intelligence and technical scoping to pricing strategy, submission, clarifications, and post-award contract management — one accountable partner across the full lifecycle.',
    accent: 'pink',
  },
  {
    icon: LineChart,
    title: 'Executive-Grade Reporting',
    desc: 'Monthly briefings with win/loss analytics, sector-wise pipeline health, and competitive intelligence — formatted for board decks and procurement review meetings.',
    accent: 'blue',
  },
];

const accentMap = {
  blue: { chip: 'linear-gradient(140deg, rgba(37,99,235,0.22) 0%, rgba(37,99,235,0.04) 100%)', ring: 'ring-blue-500/25', text: 'text-blue-400' },
  violet: { chip: 'linear-gradient(140deg, rgba(167,139,250,0.22) 0%, rgba(167,139,250,0.04) 100%)', ring: 'ring-violet-500/25', text: 'text-violet-400' },
  emerald: { chip: 'linear-gradient(140deg, rgba(52,211,153,0.22) 0%, rgba(52,211,153,0.04) 100%)', ring: 'ring-emerald-500/25', text: 'text-emerald-400' },
  amber: { chip: 'linear-gradient(140deg, rgba(245,158,11,0.22) 0%, rgba(245,158,11,0.04) 100%)', ring: 'ring-amber-500/25', text: 'text-amber-400' },
  pink: { chip: 'linear-gradient(140deg, rgba(244,114,182,0.22) 0%, rgba(244,114,182,0.04) 100%)', ring: 'ring-pink-500/25', text: 'text-pink-400' },
};

const WhyCorporates = () => {
  const { onMouseMove } = useSheen();

  return (
    <section id="why-corporates" className="relative py-24 lg:py-32 px-6 lg:px-10 overflow-hidden">
      {/* subtle aurora */}
      <div className="aurora-blob aurora-blue aurora-animate-2" style={{ width: 500, height: 500, top: '-10%', left: '-10%', opacity: 0.25 }} />

      <div className="relative max-w-[1400px] mx-auto">
        <Reveal className="text-center mb-14 lg:mb-16">
          <div className="section-num mb-5 justify-center">
            <span className="num">03</span>
            <span>Why Corporates Choose Us</span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            <span className="text-white">Built for </span>
            <span className="text-gradient-mixed">Enterprise Procurement</span>
          </h2>
          <p className="mt-5 text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Mid-to-large enterprises choose Arvanta because our operating model is engineered for
            the governance, confidentiality, and audit standards that corporate procurement teams
            require — not for the shortcuts smaller shops take.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {PILLARS.map((p, i) => {
            const a = accentMap[p.accent];
            const Icon = p.icon;
            return (
              <Reveal key={i} variant="up" delay={i * 80}>
                <div
                  onMouseMove={onMouseMove}
                  className="card-sheen svc-card glass-card glass-card-hover rounded-3xl p-7 lg:p-8 h-full"
                >
                  <div
                    className={`relative w-12 h-12 rounded-xl flex items-center justify-center mb-5 ring-1 ${a.ring}`}
                    style={{ background: a.chip }}
                  >
                    <Icon className={`w-5 h-5 ${a.text}`} />
                  </div>
                  <h3 className="font-display text-lg lg:text-xl font-bold text-white mb-3">{p.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhyCorporates;
